/**
 * Site geometry emission for GLTF export.
 * Converts site geometry from GeomTagGroup format to BuildingGeometry emissions.
 * @module
 */
import {
  bottomLevel,
  getGroundAttachedLevelGroupAssociation,
  groundLevel,
} from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import { createWrappedEmitter, type BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { GeneratedSite } from "building-types/site/site";
import type { SiteGeomTagGroup } from "building-types/site/siteGeomTagGroup";
import {
  MS_Color__C,
  MS_Floor_Concrete__Default,
  MS_Grass_Lawn_01__Default,
  MS_Gravel__Default,
  MS_Metal_Screen__Default,
  MS_Wall_VerticalBoard08__Default,
} from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceGroupNew } from "geom/brep";
import { geomToFaces } from "geom/geom";
import type GeomTagGroup from "geom/geomTagGroup";
import { UVGeneration } from "geom/uv";
import { SITE_LAYER } from "gfx/layerConstants";

/** Default materials for each site geometry type */
const DEFAULT_GRASS_MATERIAL: MaterialSrc = MS_Grass_Lawn_01__Default;
const DEFAULT_ROAD_MATERIAL: MaterialSrc = MS_Gravel__Default;
const DEFAULT_SIDEWALK_MATERIAL: MaterialSrc = MS_Floor_Concrete__Default;
const DEFAULT_CURB_MATERIAL: MaterialSrc = MS_Floor_Concrete__Default;
const DEFAULT_DRIVEWAY_MATERIAL: MaterialSrc = MS_Floor_Concrete__Default;
const DEFAULT_FENCE_MATERIAL: MaterialSrc = {
  ...MS_Wall_VerticalBoard08__Default,
  colorId: DMC.SherwinWilliams.SW_0024_CurioGray,
};
const DEFAULT_WHITEBOX_MATERIAL: MaterialSrc = MS_Metal_Screen__Default;
const DEFAULT_PLANE_MATERIAL: MaterialSrc = { ...MS_Color__C, colorId: DMC.SystemColors.White };

/** Subset of Building fields needed for site geometry emission */
export type BuildingForSiteGeometries = Pick<Building, "sites" | "exteriorPalette" | "levels">;

/**
 * Emit site geometry as BuildingGeometry for GLTF export.
 * This converts the site's GeomTagGroup-based geometry into BuildingGeometry emissions
 * that can be processed by GLTFBuildingEmitter.
 *
 * @param building - Building with sites and exteriorPalette
 * @param emitter - BuildingGeometryEmitter to receive the geometry
 */
export function getSiteGeometries(
  building: BuildingForSiteGeometries,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const { sites, exteriorPalette, levels } = building;

  if (!sites) {
    return;
  }

  const ground = groundLevel(levels);
  const bottom = bottomLevel(levels);
  const levelGroupAssociation = getGroundAttachedLevelGroupAssociation(ground?.id, bottom?.id, bottom?.floorIndex);
  const siteEmitter = levelGroupAssociation ? createWrappedEmitter(emitter, levelGroupAssociation) : emitter;

  siteEmitter.beginGroup?.("Site");

  for (const siteId in sites) {
    const site = sites[siteId];
    emitSiteGeometries(site, exteriorPalette, entityTypes, siteEmitter);
  }

  siteEmitter.endGroup?.("Site");
}

/**
 * Emit all geometry for a single site.
 */
function emitSiteGeometries(
  site: GeneratedSite,
  exteriorPalette: ExteriorPalette,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
): void {
  const sitePickHandle: DocEntityHandle = { type: "SITE", id: site.id };

  // Emit road geometry
  if (entityTypes.has(FaceCategory.SITE_ROAD)) {
    emitSiteGeomTagGroups({
      groups: site.roadGeom,
      faceCategory: FaceCategory.SITE_ROAD,
      defaultMaterial: DEFAULT_ROAD_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit grass geometry
  if (entityTypes.has(FaceCategory.SITE_GRASS)) {
    emitSiteGeomTagGroups({
      groups: site.grassGeom,
      faceCategory: FaceCategory.SITE_GRASS,
      defaultMaterial: DEFAULT_GRASS_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit sidewalk geometry
  if (entityTypes.has(FaceCategory.SITE_SIDEWALK)) {
    emitSiteGeomTagGroups({
      groups: site.sidewalkGeom,
      faceCategory: FaceCategory.SITE_SIDEWALK,
      defaultMaterial: DEFAULT_SIDEWALK_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit curb geometry
  if (entityTypes.has(FaceCategory.SITE_CURB)) {
    emitSiteGeomTagGroups({
      groups: site.curbGeom,
      faceCategory: FaceCategory.SITE_CURB,
      defaultMaterial: DEFAULT_CURB_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit driveway geometry
  if (entityTypes.has(FaceCategory.SITE_DRIVEWAY)) {
    emitSiteGeomTagGroups({
      groups: site.drivewayGeom,
      faceCategory: FaceCategory.SITE_DRIVEWAY,
      defaultMaterial: DEFAULT_DRIVEWAY_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit fence geometry
  if (entityTypes.has(FaceCategory.SITE_FENCE)) {
    emitSiteGeomTagGroups({
      groups: site.fenceGeom,
      faceCategory: FaceCategory.SITE_FENCE,
      defaultMaterial: DEFAULT_FENCE_MATERIAL,
      exteriorPalette,
      sitePickHandle,
      emitter,
    });
  }

  // Emit whitebox geometry (uses GeomTagGroup directly, not SiteGeomTagGroup)
  if (entityTypes.has(FaceCategory.SITE_WHITEBOX)) {
    emitGeomTagGroup({
      group: site.siteWhiteBoxGeom,
      faceCategory: FaceCategory.SITE_WHITEBOX,
      defaultMaterial: DEFAULT_WHITEBOX_MATERIAL,
      exteriorPalette,
      pickHandle: sitePickHandle,
      emitter,
    });
  }

  // Emit site plane geometry
  if (entityTypes.has(FaceCategory.SITE_PLANE)) {
    emitGeomTagGroup({
      group: site.sitePlaneGeom,
      faceCategory: FaceCategory.SITE_PLANE,
      defaultMaterial: DEFAULT_PLANE_MATERIAL,
      exteriorPalette,
      pickHandle: undefined, // The site plane is not pickable.
      emitter,
    });
  }

  // Emit topography geometry (uses GeomTagGroup, wrapped as SiteGeomTagGroup)
  if (entityTypes.has(FaceCategory.SITE_TOPOGRAPHY)) {
    emitGeomTagGroup({
      group: site.topographyGeom,
      faceCategory: FaceCategory.SITE_TOPOGRAPHY,
      defaultMaterial: DEFAULT_GRASS_MATERIAL,
      exteriorPalette,
      pickHandle: sitePickHandle,
      emitter,
    });
  }
}

interface EmitSiteGeomTagGroupsOptions {
  groups: SiteGeomTagGroup[];
  faceCategory: FaceCategory;
  defaultMaterial: MaterialSrc;
  exteriorPalette: ExteriorPalette;
  sitePickHandle: DocEntityHandle | undefined;
  emitter: BuildingGeometryEmitter;
}

/**
 * Emit BuildingGeometry for an array of SiteGeomTagGroup.
 * Each SiteGeomTagGroup contains a GeomTagGroup and the handle its geometry selects.
 */
function emitSiteGeomTagGroups(options: EmitSiteGeomTagGroupsOptions): void {
  const { groups, faceCategory, defaultMaterial, exteriorPalette, sitePickHandle, emitter } = options;

  if (!groups) {
    return;
  }

  for (const { group, pickHandle } of groups) {
    emitGeomTagGroup({
      group,
      faceCategory,
      defaultMaterial,
      exteriorPalette,
      pickHandle: pickHandle ?? sitePickHandle,
      emitter,
    });
  }
}

interface EmitGeomTagGroupOptions {
  group: GeomTagGroup<ExteriorMaterialOverride>;
  faceCategory: FaceCategory;
  defaultMaterial: MaterialSrc;
  exteriorPalette: ExteriorPalette;
  pickHandle: DocEntityHandle | undefined;
  emitter: BuildingGeometryEmitter;
}

/**
 * Emit BuildingGeometry for a single GeomTagGroup.
 * Resolves material from palette override or uses default.
 */
function emitGeomTagGroup(options: EmitGeomTagGroupOptions): void {
  const { group, faceCategory, defaultMaterial, exteriorPalette, pickHandle, emitter } = options;

  if (!group || group.geom.length === 0) {
    return;
  }

  const threeLayers = [SITE_LAYER];

  for (let i = 0; i < group.geom.length; i++) {
    const geom = group.geom[i];
    const tag = group.tags[i];

    // Resolve material: use palette override if available, otherwise use default
    let materialSrc: MaterialSrc = defaultMaterial;
    if (tag?.exteriorMaterial) {
      const paletteMaterial = materialSrcFromPalette(exteriorPalette, tag.exteriorMaterial);
      if (paletteMaterial) {
        materialSrc = paletteMaterial;
      }
    }

    const faces = geomToFaces(geom);
    if (faces.length === 0) {
      continue;
    }

    emitter.emit({
      geom: faceGroupNew(faces),
      faceCategory,
      threeLayers,
      pickHandle,
      materialSlot: tag?.exteriorMaterial,
      materialSrc,
      uvGen: tag?.uvGen ?? UVGeneration.GLOBAL_RECT_UV,
      materialTransformation: tag?.materialTransformation,
    });
  }
}
